import { useEffect, useRef, useCallback } from 'react';
import socketClient from '../services/socketClient';

/**
 * Custom hook for managing WebSocket event listeners in game components
 * Handles component mounting/unmounting patterns and memory leak prevention
 * Extracted from GameRoom.js and GameLobby.js duplicate code
 *
 * @param {string} roomCode - The room code for the WebSocket connection
 * @param {Object} eventHandlers - Object mapping event names to handler functions
 * @param {Array} dependencies - Dependencies for useEffect (optional)
 * @returns {Object} - { emit, connected, cleanup, addCleanupFunction, isMounted }
 */
const useGameWebSocket = (roomCode, eventHandlers = {}, dependencies = []) => {
  const mountedRef = useRef(true);
  const cleanupFunctionsRef = useRef([]);
  const registeredHandlersRef = useRef(new Map()); // Store wrapped handlers for cleanup
  const eventHandlersRef = useRef(eventHandlers); // Store handlers in ref to avoid recreating functions

  // Update the ref when eventHandlers change
  eventHandlersRef.current = eventHandlers;

  // Main effect for managing WebSocket lifecycle
  useEffect(() => {
    mountedRef.current = true;

    if (!roomCode) {
      console.warn('useGameWebSocket: No room code provided');
      return;
    }

    console.log('🔌 Setting up WebSocket event listeners for room:', roomCode);

    // Clear any existing handlers first
    registeredHandlersRef.current.forEach((handler, eventName) => {
      socketClient.off(eventName, handler);
    });
    registeredHandlersRef.current.clear();

    // Register all event handlers with safety wrapper
    Object.entries(eventHandlersRef.current).forEach(([eventName, handler]) => {
      if (typeof handler === 'function') {
        // Create safe handler inline to avoid dependency issues
        const safeHandler = (...args) => {
          if (!mountedRef.current) {
            console.log(`🚫 Ignoring ${eventName} event - component unmounted`);
            return;
          }
          // Always use the latest handler from the ref
          const currentHandler = eventHandlersRef.current[eventName];
          if (currentHandler) {
            return currentHandler(...args);
          }
        };
        socketClient.on(eventName, safeHandler);
        registeredHandlersRef.current.set(eventName, safeHandler); // Store for cleanup
        console.log(`✅ Registered handler for: ${eventName}`);
      }
    });

    console.log(
      `✅ ${Object.keys(eventHandlersRef.current).length} WebSocket event listeners registered`
    );

    // Copy ref value for cleanup to avoid stale ref warning
    const registeredHandlers = registeredHandlersRef.current;

    // Cleanup function
    return () => {
      console.log('🧹 useGameWebSocket cleanup - component unmounting');
      mountedRef.current = false;

      // Remove all registered event handlers using stored wrapped handlers
      registeredHandlers.forEach((handler, eventName) => {
        socketClient.off(eventName, handler);
      });
      registeredHandlers.clear();

      // Clean up any stored cleanup functions (for timers, intervals, etc.)
      cleanupFunctionsRef.current.forEach((cleanup) => {
        if (typeof cleanup === 'function') {
          try {
            cleanup();
          } catch (error) {
            console.error('Error during cleanup:', error);
          }
        }
      });
      cleanupFunctionsRef.current = [];

      console.log('✅ WebSocket event listeners cleaned up');
    };
  }, [roomCode]); // Only depend on roomCode, not on handlers or other dependencies

  // Emit function with safety check
  const emit = useCallback((eventName, data) => {
    if (!mountedRef.current) {
      console.warn(`🚫 Attempted to emit ${eventName} but component is unmounted`);
      return;
    }

    if (!socketClient.isSocketConnected()) {
      console.warn(`🚫 Attempted to emit ${eventName} but socket is not connected`);
      return;
    }

    socketClient.emit(eventName, data);
  }, []);

  // Connection status
  const connected = socketClient.isSocketConnected();

  // Function to add cleanup functions (for timers, intervals, etc.)
  const addCleanupFunction = useCallback((cleanupFn) => {
    if (typeof cleanupFn === 'function') {
      cleanupFunctionsRef.current.push(cleanupFn);
    }
  }, []);

  // Manual cleanup function (useful for component-specific cleanup)
  const cleanup = useCallback(() => {
    console.log('🧹 Manual cleanup requested');
    // Remove all registered event handlers
    registeredHandlersRef.current.forEach((handler, eventName) => {
      socketClient.off(eventName, handler);
    });
    registeredHandlersRef.current.clear();

    // Clean up any stored cleanup functions
    cleanupFunctionsRef.current.forEach((cleanupFn) => {
      if (typeof cleanupFn === 'function') {
        try {
          cleanupFn();
        } catch (error) {
          console.error('Error during cleanup:', error);
        }
      }
    });
    cleanupFunctionsRef.current = [];
  }, []);

  // Check if component is mounted
  const isMounted = useCallback(() => mountedRef.current, []);

  return {
    emit,
    connected,
    cleanup,
    addCleanupFunction,
    isMounted,
  };
};

export default useGameWebSocket;
